import {
  EU_OPEN_BANKING_PAYMENT_CURRENCY_CODES,
  PayableEntityType,
  PaymentsInterimBillingInterval,
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
} from "@shared_packages/toolkit/types";
import type {
  SubscriptionFeeSchedule,
  SubscriptionFeeRule,
} from "../../../../util/types";
import {
  compactScheduleForSave,
  defaultBulkmonthlyPlanKey,
  emptyFeeRule,
  ensureGbpSchedule,
  normalizeSchedule,
  payableTypeLabel,
} from "./subscription-billing.helpers";

export type PaymentsWizardCapabilities = {
  canAccessInternationalOb: boolean;
  canAccessRecurringPaymentsUk: boolean;
  canAccessVrpUk: boolean;
  canAccessBulkPaymentsUk: boolean;
};

export type PaymentsWizardProduct = "uk" | "eu";

export type PaymentsWizardDraft = {
  product: PaymentsWizardProduct;
  mode: PaymentsPricingMode;
  monthlyFeeMinor: number | null;
  fees: SubscriptionFeeSchedule[];
  enabledPaymentCurrencies: string[];
  capabilities: PaymentsWizardCapabilities;
  interimInterval: PaymentsInterimBillingInterval | null;
};

export type PaymentsWizardStepValidation = {
  valid: boolean;
  message?: string;
  fields?: string[];
};

type BuildPaymentsWizardDraftInput = PaymentsWizardDraft & {
  billingCurrency?: string | null;
};

const CONFIGURABLE_MODES = new Set<PaymentsPricingMode>([
  PaymentsPricingMode.RULE_BASED,
  PaymentsPricingMode.MONTHLY_FEE,
  PaymentsPricingMode.BESPOKE,
]);

export function isConfigurablePaymentsWizardMode(
  mode: PaymentsPricingMode
): boolean {
  return CONFIGURABLE_MODES.has(mode);
}

export function supportsInterimBillingForPaymentsDraft(
  mode: PaymentsPricingMode,
  fees: SubscriptionFeeSchedule[]
): boolean {
  return (
    mode === PaymentsPricingMode.RULE_BASED &&
    fees.every((schedule) =>
      schedule.rules.every(
        (rule) =>
          rule.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES
      )
    )
  );
}

function positiveValue(value: number | null | undefined): number | null {
  return value != null && value > 0 ? value : null;
}

function emptyCustomFeeRule(
  payableEntityType: PayableEntityType
): SubscriptionFeeRule {
  return emptyFeeRule(
    payableEntityType,
    SubscriptionFeePricingStrategy.CUSTOM_FEES
  );
}

function customFeeRule(
  payableEntityType: PayableEntityType,
  existing?: SubscriptionFeeRule
): SubscriptionFeeRule {
  return {
    payableEntityType,
    pricingStrategy: SubscriptionFeePricingStrategy.CUSTOM_FEES,
    flatFeeMinor: positiveValue(existing?.flatFeeMinor),
    percentageFee:
      existing?.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE
        ? null
        : positiveValue(existing?.percentageFee),
    monthlyPlanKey: null,
    monthlyFeeMinor: null,
    monthlyUnitLimit: null,
  };
}

function perSetupFeeRule(existing: SubscriptionFeeRule): SubscriptionFeeRule {
  return {
    payableEntityType: PayableEntityType.RECURRING_PAYMENT,
    pricingStrategy: SubscriptionFeePricingStrategy.PER_SETUP_FEE,
    flatFeeMinor: positiveValue(existing.flatFeeMinor),
    percentageFee: null,
    monthlyPlanKey: null,
    monthlyFeeMinor: null,
    monthlyUnitLimit: null,
  };
}

function tieredRule(existing: SubscriptionFeeRule): SubscriptionFeeRule {
  return {
    payableEntityType: PayableEntityType.PAYMENT,
    pricingStrategy: existing.pricingStrategy,
    flatFeeMinor: null,
    percentageFee: null,
    monthlyPlanKey: null,
    monthlyFeeMinor: null,
    monthlyUnitLimit: null,
  };
}

function bulkRule(
  existing: SubscriptionFeeRule | undefined,
  billingCurrency?: string | null
): SubscriptionFeeRule {
  if (
    existing?.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES
  ) {
    return customFeeRule(PayableEntityType.BULK_PAYMENT, existing);
  }

  if (
    existing?.pricingStrategy ===
    SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN
  ) {
    const monthlyPlanKey = existing.monthlyPlanKey ?? null;
    return {
      payableEntityType: PayableEntityType.BULK_PAYMENT,
      pricingStrategy: SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN,
      flatFeeMinor: null,
      percentageFee: null,
      monthlyPlanKey,
      monthlyFeeMinor:
        monthlyPlanKey == null ? positiveValue(existing.monthlyFeeMinor) : null,
      monthlyUnitLimit:
        monthlyPlanKey == null
          ? positiveValue(existing.monthlyUnitLimit)
          : null,
    };
  }

  return {
    payableEntityType: PayableEntityType.BULK_PAYMENT,
    pricingStrategy: SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN,
    flatFeeMinor: null,
    percentageFee: null,
    monthlyPlanKey: defaultBulkmonthlyPlanKey(billingCurrency),
    monthlyFeeMinor: null,
    monthlyUnitLimit: null,
  };
}

function requiredPayableTypes(
  currency: string,
  capabilities: PaymentsWizardCapabilities
): PayableEntityType[] {
  if (currency !== "gbp") return [PayableEntityType.PAYMENT];

  const types = [PayableEntityType.PAYMENT];
  if (capabilities.canAccessRecurringPaymentsUk) {
    types.push(PayableEntityType.RECURRING_PAYMENT);
  }
  if (capabilities.canAccessBulkPaymentsUk) {
    types.push(PayableEntityType.BULK_PAYMENT);
  }
  if (capabilities.canAccessVrpUk) {
    types.push(PayableEntityType.VRP);
  }
  return types;
}

function normalizeRuleForMode(
  mode: PaymentsPricingMode,
  currency: string,
  payableEntityType: PayableEntityType,
  existing: SubscriptionFeeRule | undefined,
  billingCurrency?: string | null
): SubscriptionFeeRule {
  if (currency !== "gbp") {
    return customFeeRule(payableEntityType, existing);
  }

  if (mode === PaymentsPricingMode.RULE_BASED) {
    if (payableEntityType === PayableEntityType.PAYMENT) {
      if (
        existing?.pricingStrategy ===
          SubscriptionFeePricingStrategy.STANDARD_TIERED ||
        existing?.pricingStrategy ===
          SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
      ) {
        return tieredRule(existing);
      }
      return customFeeRule(payableEntityType, existing);
    }

    if (
      payableEntityType === PayableEntityType.RECURRING_PAYMENT &&
      existing?.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE
    ) {
      return perSetupFeeRule(existing);
    }

    if (payableEntityType === PayableEntityType.BULK_PAYMENT) {
      return bulkRule(existing, billingCurrency);
    }

    return customFeeRule(payableEntityType, existing);
  }

  return emptyCustomFeeRule(payableEntityType);
}

function normalizeFeeSchedules(
  fees: SubscriptionFeeSchedule[],
  mode: PaymentsPricingMode,
  capabilities: PaymentsWizardCapabilities,
  enabledPaymentCurrencies: string[],
  billingCurrency?: string | null
): SubscriptionFeeSchedule[] {
  const source = ensureGbpSchedule(fees);
  const currencies = capabilities.canAccessInternationalOb
    ? Array.from(
        new Set([
          "gbp",
          ...enabledPaymentCurrencies.map((currency) => currency.toLowerCase()),
        ])
      )
    : ["gbp"];
  const schedules = currencies.map(
    (currency) =>
      source.find((schedule) => schedule.currency === currency) ?? {
        currency,
        minimumMonthlyFeeMinor: null,
        rules: [],
      }
  );

  return schedules.map((schedule) => {
    const existingRules = new Map<PayableEntityType, SubscriptionFeeRule>(
      schedule.rules.map((rule) => [rule.payableEntityType, rule] as const)
    );
    const rules = requiredPayableTypes(schedule.currency, capabilities).map(
      (payableEntityType) =>
        normalizeRuleForMode(
          mode,
          schedule.currency,
          payableEntityType,
          existingRules.get(payableEntityType),
          billingCurrency
        )
    );
    const minimumMonthlySupported = rules.every(
      (rule) =>
        rule.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES ||
        rule.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE
    );

    return {
      currency: schedule.currency,
      minimumMonthlyFeeMinor: minimumMonthlySupported
        ? positiveValue(schedule.minimumMonthlyFeeMinor)
        : null,
      rules,
    };
  });
}

function normalizeEuPaymentCurrencies(currencies: string[]): string[] {
  const supported = new Set<string>(EU_OPEN_BANKING_PAYMENT_CURRENCY_CODES);
  return Array.from(
    new Set(
      currencies
        .map((currency) => currency.toLowerCase())
        .filter((currency) => supported.has(currency))
    )
  );
}

function normalizeEuFeeSchedules(
  fees: SubscriptionFeeSchedule[],
  enabledPaymentCurrencies: string[]
): SubscriptionFeeSchedule[] {
  const source = new Map(
    fees.map((schedule) => [schedule.currency.toLowerCase(), schedule] as const)
  );

  return enabledPaymentCurrencies.map((currency) => {
    const existing = source.get(currency);
    const paymentRule = existing?.rules.find(
      (rule) => rule.payableEntityType === PayableEntityType.PAYMENT
    );
    return {
      currency,
      minimumMonthlyFeeMinor: positiveValue(existing?.minimumMonthlyFeeMinor),
      rules: [customFeeRule(PayableEntityType.PAYMENT, paymentRule)],
    };
  });
}

export function buildPaymentsWizardDraft({
  product,
  mode,
  monthlyFeeMinor,
  fees,
  enabledPaymentCurrencies,
  capabilities,
  interimInterval,
  billingCurrency,
}: BuildPaymentsWizardDraftInput): PaymentsWizardDraft {
  if (product === "eu") {
    const currencies = normalizeEuPaymentCurrencies(enabledPaymentCurrencies);
    const euCapabilities: PaymentsWizardCapabilities = {
      canAccessInternationalOb: true,
      canAccessRecurringPaymentsUk: false,
      canAccessVrpUk: false,
      canAccessBulkPaymentsUk: false,
    };
    const euFees =
      mode === PaymentsPricingMode.RULE_BASED
        ? normalizeEuFeeSchedules(fees, currencies)
        : [];
    // Mirrors the UK branch below — EU rule-based pricing is always custom
    // fees only (enforced by the fee editor itself), so this is really just
    // gating on `mode`, but computing it the same way UK does keeps both
    // branches honest if that constraint ever changes.
    const supportsInterim = supportsInterimBillingForPaymentsDraft(
      mode,
      euFees
    );

    return {
      product,
      mode,
      monthlyFeeMinor:
        mode === PaymentsPricingMode.MONTHLY_FEE
          ? positiveValue(monthlyFeeMinor)
          : null,
      fees: euFees,
      enabledPaymentCurrencies: currencies,
      capabilities: euCapabilities,
      interimInterval: supportsInterim ? interimInterval : null,
    };
  }

  const normalizedCapabilities: PaymentsWizardCapabilities = {
    ...capabilities,
    canAccessInternationalOb: false,
  };

  if (mode !== PaymentsPricingMode.RULE_BASED) {
    return {
      product,
      mode,
      monthlyFeeMinor:
        mode === PaymentsPricingMode.MONTHLY_FEE
          ? positiveValue(monthlyFeeMinor)
          : null,
      fees: [],
      enabledPaymentCurrencies: ["gbp"],
      capabilities: normalizedCapabilities,
      interimInterval: null,
    };
  }

  const normalizedFees = normalizeFeeSchedules(
    normalizeSchedule(fees),
    mode,
    normalizedCapabilities,
    ["gbp"],
    billingCurrency
  );
  const supportsInterim = supportsInterimBillingForPaymentsDraft(
    mode,
    normalizedFees
  );

  return {
    product,
    mode,
    monthlyFeeMinor: null,
    fees: normalizedFees,
    enabledPaymentCurrencies: ["gbp"],
    capabilities: normalizedCapabilities,
    interimInterval: supportsInterim ? interimInterval : null,
  };
}

function hasPositiveCustomFee(rule: SubscriptionFeeRule): boolean {
  return (
    positiveValue(rule.flatFeeMinor) != null ||
    positiveValue(rule.percentageFee) != null
  );
}

function hasValidPerSetupFee(rule: SubscriptionFeeRule): boolean {
  return (
    rule.payableEntityType === PayableEntityType.RECURRING_PAYMENT &&
    positiveValue(rule.flatFeeMinor) != null &&
    rule.percentageFee == null &&
    rule.monthlyPlanKey == null &&
    rule.monthlyFeeMinor == null &&
    rule.monthlyUnitLimit == null
  );
}

function hasValidFixedMonthlyPlan(rule: SubscriptionFeeRule): boolean {
  const cataloguePlan = rule.monthlyPlanKey != null;
  const customPlan =
    positiveValue(rule.monthlyFeeMinor) != null &&
    positiveValue(rule.monthlyUnitLimit) != null;

  return (
    rule.payableEntityType === PayableEntityType.BULK_PAYMENT &&
    rule.flatFeeMinor == null &&
    rule.percentageFee == null &&
    (cataloguePlan || customPlan)
  );
}

function validateRule(
  mode: PaymentsPricingMode,
  payableEntityType: PayableEntityType,
  rule: SubscriptionFeeRule | undefined
): boolean {
  if (!rule) return false;

  if (rule.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES) {
    return hasPositiveCustomFee(rule);
  }
  if (rule.pricingStrategy === SubscriptionFeePricingStrategy.PER_SETUP_FEE) {
    return hasValidPerSetupFee(rule);
  }
  if (
    rule.pricingStrategy === SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN
  ) {
    return hasValidFixedMonthlyPlan(rule);
  }
  return (
    payableEntityType === PayableEntityType.PAYMENT &&
    (rule.pricingStrategy === SubscriptionFeePricingStrategy.STANDARD_TIERED ||
      rule.pricingStrategy === SubscriptionFeePricingStrategy.HIGH_RISK_TIERED)
  );
}

export function validatePaymentsWizardStep(
  draft: PaymentsWizardDraft,
  scope: "uk" | "eu",
  billingCurrency?: string | null
): PaymentsWizardStepValidation {
  if (!CONFIGURABLE_MODES.has(draft.mode)) return { valid: true };

  const normalized = buildPaymentsWizardDraft({
    ...draft,
    billingCurrency,
  });

  if (scope === "uk" && normalized.product === "eu") {
    return { valid: true };
  }
  if (scope === "eu" && normalized.product === "uk") {
    return { valid: true };
  }

  if (
    normalized.product === "eu" &&
    normalized.enabledPaymentCurrencies.length === 0
  ) {
    return {
      valid: false,
      message: "Select at least one EU payment currency.",
      fields: ["enabledPaymentCurrencies"],
    };
  }
  const scopedSchedules = normalized.fees.filter((schedule) =>
    scope === "uk" ? schedule.currency === "gbp" : schedule.currency !== "gbp"
  );

  if (
    normalized.product === "uk" &&
    scope === "eu" &&
    !normalized.capabilities.canAccessInternationalOb
  ) {
    return { valid: true };
  }

  if (
    scope === "eu" &&
    normalized.enabledPaymentCurrencies.every((currency) => currency === "gbp")
  ) {
    return {
      valid: false,
      message: "Select at least one EU currency, or disable International OB.",
      fields: ["enabledPaymentCurrencies"],
    };
  }

  if (normalized.mode === PaymentsPricingMode.MONTHLY_FEE) {
    if (scope === "eu" && !billingCurrency) {
      return {
        valid: false,
        message:
          "A fixed monthly fee needs a billing currency — pick GBP, EUR or USD, or switch to Rule based pricing.",
        fields: ["monthlyFeeMinor"],
      };
    }
    return positiveValue(normalized.monthlyFeeMinor) != null
      ? { valid: true }
      : {
          valid: false,
          message: "Enter a monthly subscription fee before continuing.",
          fields: ["monthlyFeeMinor"],
        };
  }
  if (normalized.mode === PaymentsPricingMode.BESPOKE) {
    return { valid: true };
  }

  if (scopedSchedules.length === 0) {
    return scope === "uk"
      ? {
          valid: false,
          message: "Configure GBP pricing before continuing.",
          fields: ["fees.gbp"],
        }
      : {
          valid: false,
          message:
            "Select and configure at least one EU currency, or disable International OB.",
          fields: ["fees.eu"],
        };
  }

  for (const schedule of scopedSchedules) {
    if (
      positiveValue(schedule.minimumMonthlyFeeMinor) != null &&
      schedule.rules.some(
        (rule) =>
          rule.pricingStrategy !== SubscriptionFeePricingStrategy.CUSTOM_FEES &&
          rule.pricingStrategy !== SubscriptionFeePricingStrategy.PER_SETUP_FEE
      )
    ) {
      return {
        valid: false,
        message: `Monthly minimum is only available with custom or per-setup fees for ${schedule.currency.toUpperCase()}.`,
        fields: [`fees.${schedule.currency}.minimumMonthlyFeeMinor`],
      };
    }
    for (const payableEntityType of requiredPayableTypes(
      schedule.currency,
      normalized.capabilities
    )) {
      const rule = schedule.rules.find(
        (entry) => entry.payableEntityType === payableEntityType
      );
      if (!validateRule(normalized.mode, payableEntityType, rule)) {
        return {
          valid: false,
          message: `Enter a valid pricing setup for ${schedule.currency.toUpperCase()} ${payableTypeLabel(
            payableEntityType
          )}.`,
          fields: [`fees.${schedule.currency}.rules.${payableEntityType}`],
        };
      }
    }
  }

  return { valid: true };
}

export function preparePaymentsWizardDraftForSave(
  draft: PaymentsWizardDraft,
  billingCurrency?: string | null
): PaymentsWizardDraft & { feeSchedules: SubscriptionFeeSchedule[] } {
  const normalized = buildPaymentsWizardDraft({
    ...draft,
    billingCurrency,
  });
  const feeSchedules =
    normalized.mode === PaymentsPricingMode.RULE_BASED
      ? compactScheduleForSave(normalized.fees)
      : [];

  return {
    ...normalized,
    feeSchedules,
  };
}
